import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import * as ShipmentTimelineEventsActions from './shipment-timeline-events.actions';
import { ShipmentTimelineEvent } from './shipment-timeline-events.models';

export const SHIPMENTTIMELINEEVENTS_FEATURE_KEY = 'shipmentTimelineEvents';

export interface State extends EntityState<ShipmentTimelineEvent> {
    selectedId?: string | number; // which ShipmentTimelineEvents record has been selected
    loading: boolean; // has the ShipmentTimelineEvents list been loaded
    error?: string | null; // last known error (if any)
}

export interface ShipmentTimelineEventsPartialState {
    readonly [SHIPMENTTIMELINEEVENTS_FEATURE_KEY]: State;
}

export const shipmentTimelineEventsAdapter: EntityAdapter<ShipmentTimelineEvent> =
    createEntityAdapter<ShipmentTimelineEvent>();

export const initialState: State =
    shipmentTimelineEventsAdapter.getInitialState({
        // set initial required properties
        loading: false,
    });

const shipmentTimelineEventsReducer = createReducer(
    initialState,
    on(ShipmentTimelineEventsActions.init, state => ({
        ...state,
        loading: true,
        error: null,
    })),
    on(
        ShipmentTimelineEventsActions.loadShipmentTimelineEventsSuccess,
        (state, { shipmentTimelineEvents }) => {
            return shipmentTimelineEventsAdapter.setAll(
                shipmentTimelineEvents,
                {
                    ...state,
                    loading: false,
                }
            );
        }
    ),
    on(ShipmentTimelineEventsActions.updateShipmentTimelineEvent, state => ({
        ...state,
        loading: true,
    })),
    on(ShipmentTimelineEventsActions.confirmShipmentTimelineEvent, state => ({
        ...state,
        loading: true,
    })),
    on(ShipmentTimelineEventsActions.createShipmentTimelineEvent, state => ({
        ...state,
        loading: true,
    })),
    on(
        ShipmentTimelineEventsActions.updateShipmentTimelineEventSuccess,
        (state, { shipmentTimelineEvent }) =>
            shipmentTimelineEventsAdapter.addOne(shipmentTimelineEvent, {
                ...state,
                loading: false,
                selectedId: null,
            })
    ),
    on(
        ShipmentTimelineEventsActions.updateShipmentTimelineEventSuccess,
        (state, { shipmentTimelineEvent }) =>
            shipmentTimelineEventsAdapter.updateOne(
                {
                    id: shipmentTimelineEvent.id,
                    changes: shipmentTimelineEvent,
                },
                {
                    ...state,
                    loading: false,
                    selectedId: null,
                }
            )
    ),
    on(
        ShipmentTimelineEventsActions.confirmShipmentTimelineEventSuccess,
        (state, { shipmentTimelineEvent }) =>
            shipmentTimelineEventsAdapter.updateOne(
                {
                    id: shipmentTimelineEvent.id,
                    changes: shipmentTimelineEvent,
                },
                {
                    ...state,
                    loading: false,
                    selectedId: null,
                }
            )
    ),
    on(
        ShipmentTimelineEventsActions.loadShipmentTimelineEventsFailure,
        ShipmentTimelineEventsActions.updateShipmentTimelineEventFailure,
        ShipmentTimelineEventsActions.confirmShipmentTimelineEventFailure,
        ShipmentTimelineEventsActions.createShipmentTimelineEventFailure,
        (state, { error }) => ({ ...state, error, loading: false })
    ),

    on(
        ShipmentTimelineEventsActions.selectShipmentTimelineEvent,
        (state, { selectedId }) => ({
            ...state,
            selectedId,
        })
    ),
    on(ShipmentTimelineEventsActions.unselectShipmentTimelineEvent, state => ({
        ...state,
        selectedId: null,
    })),
    on(ShipmentTimelineEventsActions.reset, () => initialState),
    on(ShipmentTimelineEventsActions.dismissError, state => ({
        ...state,
        error: null,
    }))
);

export function reducer(state: State | undefined, action: Action) {
    return shipmentTimelineEventsReducer(state, action);
}
